import { memo } from "react";

import { Button } from "@components/Button";
import { CollectionAdjacentCollectionButton } from "@react/sections/Collection/CollectionFilterNavigation/CollectionAdjacentCollectionButton.data";
import { CollectionAdjacentCollectionsPlaceholder } from "@react/sections/Collection/CollectionFilterNavigation/CollectionAdjacentCollectionsPlaceholder.view";
import { type TTailwindString, tw } from "classnames";

type Props = {
  adjacentCollections:
    | {
        handle: string;
        id: string;
        onlineStoreUrl: string | null | undefined;
        title: string;
      }[]
    | null;
  filterButtonText: string;
  filtersActive: boolean;
  handleAdjacentCollectionChange: (collection: {
    handle: string;
    id: string;
    onlineStoreUrl: string | null | undefined;
    title: string;
  }) => void;
  isLoading: boolean;
  onFiltersToggleClick: () => void;
};

const styles = {
  base: tw(
    "sticky",
    "top-[56px]" as TTailwindString,
    "mg:top-[65px]" as TTailwindString,
    "z-10",
    "mt-4",
    "border-0",
    "border-b",
    "border-solid",
    "border-divider",
    "bg-background-primary"
  ),
  innerContainer: tw(
    "flex",
    "items-center",
    "mx-auto",
    "max-w-screen-xl",
    "px-6",
    "py-4",
    "mg:px-12",
    "mg:py-[21.5px]" as TTailwindString
  ),
  filterButtonContainer: tw("relative"),
  adjacentCollectionButtons: tw(
    "flex",
    "gap-3",
    "pl-4",
    "pr-6",
    "py-1",
    "ml-4",
    "-mr-6",
    "mg:-mr-12",
    "mg:pr-12",
    "lg:pl-6",
    "lg:ml-6",
    "border-0",
    "border-l",
    "border-solid",
    "border-divider",
    "overflow-x-auto",
    "relative"
  ),
  adjacentCollectionButton: tw("shrink-0"),
  gradient: tw(
    "absolute",
    "w-4",
    "lg:w-6",
    "h-8",
    "bg-[linear-gradient(to_right,#fcfaf6_0%,rgba(252,250,246,0)_100%)]" as TTailwindString,
    "left-[calc(100%+17px)]" as TTailwindString,
    "lg:left-[calc(100%+25px)]" as TTailwindString,
    "top-0",
    "z-10"
  ),
};

const CollectionFilterNavigationBase = ({
  adjacentCollections,
  filterButtonText,
  filtersActive,
  handleAdjacentCollectionChange,
  isLoading,
  onFiltersToggleClick,
}: Props) => {
  // Helper function to check if a collection is currently active
  const isActiveCollection = (handle: string): boolean => {
    if (typeof window === "undefined" || !window.location) return false;
    const currentPath = window.location.pathname;
    const collectionPath = `/collections/${handle}`;
    return currentPath === collectionPath;
  };

  return (
    <div className={styles.base}>
      <div className={styles.innerContainer}>
        <div className={styles.filterButtonContainer}>
          <Button
            aria-expanded={filtersActive}
            aria-pressed={filtersActive}
            iconName="filter"
            iconPosition="end"
            onClick={onFiltersToggleClick}
            size="small"
            type={filtersActive ? "primary" : "secondary"}
          >
            {filterButtonText}
          </Button>
          <div className={styles.gradient} />
        </div>
        <div className={styles.adjacentCollectionButtons}>
          {!isLoading || adjacentCollections ? (
            adjacentCollections?.map((collection) => (
              <CollectionAdjacentCollectionButton
                adjacentCollection={collection}
                handleAdjacentCollectionChange={handleAdjacentCollectionChange}
                isActive={isActiveCollection(collection.handle)}
                key={`adjacentCollectionButton-${collection.id}`}
              />
            ))
          ) : (
            <CollectionAdjacentCollectionsPlaceholder />
          )}
        </div>
      </div>
    </div>
  );
};

export const CollectionFilterNavigation = memo(CollectionFilterNavigationBase);
